import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const EDGE_GAP = 8;
const ICON_GAP = 6;

/**
 * The bubble is rendered into document.body rather than next to the icon.
 * Fields sit inside containers that clip (`.tab-content` needs overflow: hidden
 * for its pane transitions), so an absolutely positioned bubble gets cut off at
 * their edges - and `position: fixed` would not escape either, because
 * `.tab-pane` carries a transform and so becomes the containing block for it.
 */
const TooltipBubble = ({ text, anchor }) => {
  const bubbleRef = useRef(null);
  // Hidden for the first pass so the un-clamped position is never painted.
  const [placement, setPlacement] = useState({ ...anchor, visibility: 'hidden' });

  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!bubble) return;

    const halfWidth = bubble.getBoundingClientRect().width / 2;
    const minLeft = EDGE_GAP + halfWidth;
    const maxLeft = window.innerWidth - EDGE_GAP - halfWidth;

    setPlacement({
      top: anchor.top,
      // Keep the bubble inside the viewport when the icon sits near an edge
      left: Math.min(Math.max(anchor.left, minLeft), maxLeft),
      visibility: 'visible'
    });
  }, [anchor]);

  return (
    <div ref={bubbleRef} className="tooltip-content is-floating" style={placement}>
      {text}
    </div>
  );
};

const FieldTooltip = ({ text }) => {
  const iconRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const show = useCallback(() => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAnchor({ top: rect.bottom + ICON_GAP, left: rect.left + rect.width / 2 });
  }, []);

  const hide = useCallback(() => setAnchor(null), []);

  return (
    <span className="tooltip-container" onMouseEnter={show} onMouseLeave={hide}>
      <i ref={iconRef} className="bi bi-info-circle tooltip-icon"></i>
      {anchor && createPortal(<TooltipBubble text={text} anchor={anchor} />, document.body)}
    </span>
  );
};

export default FieldTooltip;
